import { Box, MenuItem, styled } from '@mui/material'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { MainComponentFilters } from 'src/generated-types/main-component'
import { SelectField } from 'src/components/SelectField'
import { FacilityTypes } from 'src/enums'
import { GroupHeader, GroupItems } from 'src/screens/localities/positions/components/MainComponentSendModal'

interface DropdownFilterProps {
  field: string
  options: Array<{ value: number | string; label: string }>
  headerFilters: MainComponentFilters
  updateFilter: (field: string, value: number | number[] | string | string[]) => void
  multiple?: boolean
  loading?: boolean
  handleChangeFields?: (event: React.SyntheticEvent, value: any, field: string, multiple: boolean) => void
  getCurrentValuesOfField?: (
    field: string,
    multiple: boolean,
    options: Array<{ value: number | string; label: string }>,
  ) => void
}

const DropdownFilter: FC<DropdownFilterProps> = ({
  field,
  options,
  headerFilters,
  updateFilter,
  multiple = false,
  loading = false,
  getCurrentValuesOfField,
  handleChangeFields,
}) => {
  const { t: translate } = useTranslation()

  const getTypeFromWhereValue = (value: string) => {
    const parts = value.split('#')
    return parts[1] || 'unknown'
  }

  const getCurrentValue = () => {
    const filterValue = headerFilters[field as keyof MainComponentFilters]

    if (field === 'where') {
      if (Array.isArray(filterValue)) {
        return (filterValue as string[])
          .map(whereValue => options.find(option => option.value === whereValue))
          .filter(Boolean)
      }
      return multiple ? [] : null
    } else if (field === 'status') {
      if (Array.isArray(filterValue)) {
        return (filterValue as number[])
          .map(statusValue => options.find(option => Number(option.value) === statusValue))
          .filter(Boolean)
      }
      return multiple ? [] : null
    } else {
      if (filterValue) {
        const matchingOption = options.find(option => String(option.value) === String(filterValue))
        return multiple ? (matchingOption ? [matchingOption] : []) : matchingOption || null
      }
      return multiple ? [] : null
    }
  }

  const currentValue = getCurrentValuesOfField ? getCurrentValuesOfField(field, multiple, options) : getCurrentValue()

  const getOptionLabel = (option: any) => {
    if (!option) return ''
    if (typeof option === 'string') return option
    return option?.label || ''
  }

  const renderOption = (props: any, option: any) => {
    return (
      <MenuItem key={option.id} value={option.id} {...props}>
        {option.label}
      </MenuItem>
    )
  }

  const groupBy =
    field === 'where'
      ? (option: any) => {
          const type = getTypeFromWhereValue(String(option.value))
          const typeNumber = parseInt(type)

          switch (typeNumber) {
            case FacilityTypes.locality:
              return translate('localities', { ns: 'common' })
            case FacilityTypes.serviceStation:
              return translate('service-stations', { ns: 'header' })
            case FacilityTypes.storage:
              return translate('storages', { ns: 'storage' })
            default:
              return translate('other', { ns: 'common' })
          }
        }
      : undefined

  const handleChange = (event: any, value: any) => {
    if (event) {
      event.stopPropagation()
      event.preventDefault()
    }

    if (field === 'where') {
      if (multiple) {
        const arrayValue = Array.isArray(value) ? value : value ? [value] : []
        const whereStrings: string[] = arrayValue
          .filter(v => v && typeof v === 'object' && 'value' in v)
          .map(v => String(v.value))
        updateFilter(field, whereStrings)
      } else {
        if (value && typeof value === 'object' && 'value' in value) {
          const whereStrings: string[] = [String(value.value)]
          updateFilter(field, whereStrings)
        } else {
          updateFilter(field, [])
        }
      }
    } else if (field === 'status') {
      if (multiple) {
        const arrayValue = Array.isArray(value) ? value : value ? [value] : []
        const statusValues = arrayValue
          .filter(v => v && typeof v === 'object' && 'value' in v)
          .map(v => Number(v.value))
        updateFilter(field, statusValues)
      } else {
        if (value && typeof value === 'object' && 'value' in value) {
          updateFilter(field, [Number(value.value)])
        } else {
          updateFilter(field, [])
        }
      }
    } else {
      if (multiple) {
        const arrayValue = Array.isArray(value) ? value : value ? [value] : []
        const stringValues = arrayValue
          .filter(v => v && typeof v === 'object' && 'value' in v)
          .map(v => String(v.value))
        updateFilter(field, stringValues.length > 0 ? stringValues.join(',') : '')
      } else {
        const extractedValue =
          value && typeof value === 'object' && 'value' in value ? String(value.value) : String(value || '')
        updateFilter(field, extractedValue)
      }
    }
  }

  return (
    <Box sx={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
      <StyledSelectField
        value={currentValue}
        onChange={
          handleChangeFields ? (event, value) => handleChangeFields(event, value, field, multiple) : handleChange
        }
        data={options}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        groupBy={groupBy}
        renderGroup={params => (
          <li>
            <GroupHeader>{params.group}</GroupHeader>
            <GroupItems>{params.children}</GroupItems>
          </li>
        )}
        variant="outlined"
        size="small"
        multiple={multiple}
        loading={loading}
        renderTags={() => null}
        placeholder={
          multiple && Array.isArray(currentValue) && currentValue.length > 0
            ? `${currentValue.length} ${translate('selected', { ns: 'common' })}`
            : undefined
        }
        noOptionsText={loading ? translate('loading', { ns: 'common' }) : translate('noOptions', { ns: 'common' })}
        isOptionEqualToValue={(option: any, value: any) => {
          if (!option || !value) return false
          return option.value === value.value
        }}
        listboxMinWidth="200px"
        disableClearable={!!currentValue}
      />
    </Box>
  )
}

export const StyledSelectField = styled(SelectField)(() => ({
  '& .MuiBox-root': {
    padding: '0 !important',
  },
  '& .MuiPaper-root': {
    marginTop: '4px !important',
  },
  '& .MuiInputBase-root': {
    minWidth: '100px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    top: '-3px',
    bottom: '-3px',
  },
  '& .MuiAutocomplete-inputRoot': {
    padding: '0 !important',
    width: '100% !important',
    '& .MuiAutocomplete-input': {
      padding: '5px 8px !important',
      width: '100% !important',
      minWidth: '100% !important',
    },
  },
}))

export default DropdownFilter